import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Users, Award, Briefcase, GraduationCap } from 'lucide-react';
import { getAllPeople, getAllCategories, getPeopleByCategory } from '../data/peopleData';

const PeopleIndexPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const allPeople = getAllPeople();
  const categories = ['All', ...getAllCategories()];
  
  const filteredPeople = selectedCategory === 'All' 
    ? allPeople 
    : getPeopleByCategory(selectedCategory);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Leadership Council': return Briefcase;
      case 'Honorary Vice Presidents': return Award;
      case 'Senior Staff': return Users;
      case 'Research Assistants': return GraduationCap;
      case 'Fellows & Visiting Scholars': return Award;
      default: return User;
    }
  };

  const PersonCard = ({ person }) => {
    const CategoryIcon = getCategoryIcon(person.category);
    
    return (
      <Link to={`/people/${person.id}`}>
        <div className="bg-white rounded-lg border border-stone-200 hover:border-green-500 hover:shadow-xl transition-all duration-200 overflow-hidden h-full">
          {/* Image */}
          <div className="aspect-square bg-stone-100 overflow-hidden">
            {person.image ? (
              <img 
                src={person.image} 
                alt={person.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-20 h-20 text-stone-300" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CategoryIcon className="w-4 h-4 text-green-600" />
              <span className="text-xs text-stone-500 uppercase tracking-wider">
                {person.category}
              </span>
            </div>
            <h3 className="font-serif font-bold text-lg text-stone-900 mb-1">
              {person.honorific && <span className="text-stone-600">{person.honorific} </span>}
              {person.name}
            </h3>
            <p className="text-sm text-stone-600">
              {person.title}
            </p>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-green-900 to-green-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-serif font-bold mb-4">People</h1>
            <p className="text-xl text-green-100 leading-relaxed">
              The Highland Institute's community of scholars, researchers, and practitioners 
              working across disciplines to advance knowledge in the highlands of Northeast India and beyond.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                {category}
                <span className="ml-2 text-xs opacity-75">
                  ({category === 'All' ? allPeople.length : getPeopleByCategory(category).length})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* People Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <p className="text-center text-stone-600">
            Showing <span className="font-bold text-green-600">{filteredPeople.length}</span> {filteredPeople.length === 1 ? 'person' : 'people'}
            {selectedCategory !== 'All' && <span> in <span className="font-medium">{selectedCategory}</span></span>}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {filteredPeople.map((person) => (
            <PersonCard key={person.id} person={person} />
          ))}
        </div>

        {filteredPeople.length === 0 && (
          <div className="text-center py-20">
            <User className="w-16 h-16 text-stone-400 mx-auto mb-4" />
            <h3 className="text-xl font-serif font-bold text-stone-900 mb-2">
              No people found
            </h3>
            <p className="text-stone-600">
              Try selecting a different category.
            </p>
          </div>
        )}
      </div>

      {/* Footer Note */}
      <div className="bg-white border-t border-stone-200 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-stone-600 font-serif leading-relaxed">
              The Highland Institute is a community-based research institution. 
              Our people page reflects ongoing collaborations and evolving scholarly relationships 
              across disciplines, geographies, and knowledge systems.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleIndexPage;
